/**
 * Thu thập bằng chứng phía thiết bị: GPS, vân tay thiết bị, cờ nghi vấn.
 *
 * GIỚI HẠN TRUNG THỰC CỦA TRÌNH DUYỆT — phải hiểu trước khi tin vào kết quả:
 *
 *  · KHÔNG đọc được BSSID. Web API không cho phép. Tín hiệu +20 điểm của
 *    `bssid_ok` chỉ có khi bọc Capacitor (docs/00 QĐ-06). Trên PWA thuần,
 *    lớp WiFi được thay bằng token beacon + IP egress.
 *  · KHÔNG phát hiện được mock location đáng tin. `isFromMockProvider` là
 *    API native của Android. Ở đây chỉ có heuristic yếu — server mới là nơi
 *    quyết định, đừng dựa vào cờ này để chặn người dùng.
 *  · KHÔNG phát hiện được root/jailbreak. Cần API native.
 *
 * Vì vậy: PWA THU THẬP, SERVER QUYẾT ĐỊNH. Không bao giờ chặn ở client.
 */

import { uuidV4 } from './uuid'

const DEVICE_ID_KEY = 'chamcong.device_id'

/**
 * Vân tay thiết bị. Ổn định trong phạm vi một trình duyệt trên một máy.
 *
 * Đây KHÔNG phải cơ chế bảo mật — người dùng xoá được, đổi được. Nó chỉ để
 * gắn "một nhân viên một thiết bị" (docs/05 §5.1) và tăng chi phí gian lận,
 * không phải để chặn (docs/00 §3 — chấm hộ không chặn được bằng phần mềm).
 */
export function layDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = uuidV4()
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

export interface ViTri {
  lat: number
  lng: number
  accuracy: number
  /** Heuristic yếu, chỉ để gợi ý — server mới quyết định (xem đầu file). */
  nghi_gia_mao: boolean
}

export type LyDoLoiViTri = 'tu_choi' | 'khong_ho_tro' | 'het_gio' | 'khong_lay_duoc'

export class LoiViTri extends Error {
  readonly ly_do: LyDoLoiViTri

  constructor(ly_do: LyDoLoiViTri, message: string) {
    super(message)
    this.name = 'LoiViTri'
    this.ly_do = ly_do
  }
}

/**
 * Lấy toạ độ GPS.
 *
 * `enableHighAccuracy` bật để dùng GPS thật thay vì suy ra từ WiFi/cell —
 * quan trọng vì geofence công trình chỉ 250 m.
 */
export function layViTri(timeoutMs = 10_000): Promise<ViTri> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new LoiViTri('khong_ho_tro', 'Thiết bị không hỗ trợ định vị'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos.coords
        resolve({
          lat: c.latitude,
          lng: c.longitude,
          accuracy: c.accuracy,
          nghi_gia_mao: nghiGiaMao(pos),
        })
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            reject(new LoiViTri('tu_choi',
              'Bạn chưa cho phép truy cập vị trí. Vào Cài đặt để bật lại.'))
            break
          case err.TIMEOUT:
            reject(new LoiViTri('het_gio',
              'Không lấy được vị trí. Ra chỗ thoáng rồi thử lại.'))
            break
          default:
            reject(new LoiViTri('khong_lay_duoc',
              'Không lấy được vị trí. Kiểm tra đã bật GPS chưa.'))
        }
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    )
  })
}

/**
 * Heuristic phát hiện giả mạo vị trí — YẾU, chỉ để gợi ý server xem kỹ.
 *
 * Ứng dụng giả mạo GPS thường trả toạ độ có độ chính xác đẹp bất thường
 * (đúng 1 m) và không có tốc độ/hướng. Người thật cầm điện thoại hiếm khi
 * đạt độ chính xác dưới 3 m ngoài trời.
 */
function nghiGiaMao(pos: GeolocationPosition): boolean {
  const c = pos.coords
  const doChinhXacDepBatThuong = c.accuracy > 0 && c.accuracy <= 1
  const thieuMoiTinHieuPhu = c.speed === null && c.heading === null && c.altitude === null
  return doChinhXacDepBatThuong && thieuMoiTinHieuPhu
}

/** Nén ảnh selfie trước khi xếp hàng — tiết kiệm băng thông và dung lượng IndexedDB. */
export async function nenAnh(
  file: Blob,
  maxCanh = 720,
  chatLuong = 0.7,
): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const tyLe = Math.min(1, maxCanh / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * tyLe)
  const h = Math.round(bitmap.height * tyLe)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close()

  // Vẽ lại qua canvas cũng chính là cách XOÁ EXIF (docs/04 §4.9 — Nghị định 13).
  // Server vẫn đóng dấu lại giờ + toạ độ, không tin metadata do máy gửi lên.
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob ?? file),
      'image/webp',
      chatLuong,
    )
  })
}

/** Giờ đồng hồ máy lúc bấm nút. Server so lệch với giờ thật (docs/03 E10). */
export function gioMay(): number {
  return Math.floor(Date.now() / 1000)
}
